'use client';

import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { StandaloneSupport } from './_components/appointment-items/standalone-support.component';
import { getAppointmentLocation } from './_services/get-appointment-location.service';

import { CardAppointmentHeader, CardAppointmentHeaderProps } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/card-appointment-header.component';
import { DetailsAccordion } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/details-accordion.component';
import { EditableField } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/editable-field.component';
import { GuideCodeField } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/guide-code-field.component';
import { LocationBlock } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/location-block.component';
import { AppointmentCardActions } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/card-appointment-actions.component';
import { AppointmentCardExpanded } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/card-appointment-expanded.component';
import { CardAppointmentProps } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/card-appointment.component';
import { Card } from '@/components/ui/card.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { formatClientName } from '@/utils/formatters/format-to-title-case';
import { professionalDisplayName } from '@/utils/formatters/professional-name';
import { partnerDisplayName } from '@/utils/formatters/appointment-entity';
import {
  getPartnerProfilePath,
  getProfessionalProfilePath,
} from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_utils/appointment-entity-path';

export function CardAppointmentStandalone({ appointment, defaultOpen, onAppointmentUpdate }: CardAppointmentProps) {
  const procedureName = appointment.items.map((item) => item.procedure.name).join(', ');
  const patientName = formatClientName(appointment.patient);
  const location = getAppointmentLocation(appointment);
  const entityName = appointment.professional
    ? professionalDisplayName(appointment.professional)
    : partnerDisplayName(appointment.partner);
  const headerEntity: CardAppointmentHeaderProps = appointment.professional
    ? {
        avatarSrc: appointment.professional.avatar,
        avatarAlt: entityName,
        avatarType: EntityTypeEnum.PROFESSIONAL,
        name: entityName,
        href: getProfessionalProfilePath(appointment.professional),
      }
    : {
        avatarSrc: appointment.partner.avatar,
        avatarAlt: entityName,
        avatarType: EntityTypeEnum.LABORATORY_COLLECT_POINT,
        name: entityName,
        href: getPartnerProfilePath(appointment),
      };

  return (
    <AppointmentCardActions appointment={appointment} onAppointmentUpdate={onAppointmentUpdate}>
      {({ onEditPatient }) => (
        <Card
          data-testid={DataTestIdEnum.APPOINTMENT_CARD_STANDALONE}
          className="flex flex-col gap-4 rounded-2xl p-4 w-full"
        >
          <div className="flex flex-col gap-4">
            <CardAppointmentHeader {...headerEntity} />

            <Typography variant={TypographyVariant.BODY_TEXT} className="font-normal">{procedureName}</Typography>

            <hr className="border-t border-gray-200 w-full" />

            <GuideCodeField appointment={appointment} />

            <EditableField
              label="Atendimento para"
              value={patientName}
              onEdit={onEditPatient}
              editAriaLabel="Editar paciente"
              editTestId={`${DataTestIdEnum.APPOINTMENT_CARD_EDIT_PATIENT}-${appointment.id}`}
            />

            <LocationBlock {...location} />

            <StandaloneSupport
              entityName={entityName}
              partner={appointment.partner}
              guideCode={appointment.referral}
              documentNumber={appointment.patient.documentNumber}
            />
          </div>

          <DetailsAccordion defaultOpen={defaultOpen}>
            <AppointmentCardExpanded appointment={appointment} onEditPatient={onEditPatient} />
          </DetailsAccordion>
        </Card>
      )}
    </AppointmentCardActions>
  );
}
